import { useState } from 'react';
import NavBar from '../components/NavBar';
import { getDesignTokens } from '../utils/designTokens';
import { useThemeStore } from '../store/themeStore';
import { Icon } from '../utils/icons';
import MemberAvatar from '../components/MemberAvatar';
import { EventMember } from '../types/calendar';
import { Todo, TodoStatus, TodoPriority, TodoList } from '../types/todo';
import BottomSheet from '../components/mobile/BottomSheet';

// Mock data - simulating 100+ todos
const mockMembers: EventMember[] = [
  { memberId: '1', memberName: 'Emma', memberColor: '#3b82f6' },
  { memberId: '2', memberName: 'Lucas', memberColor: '#ef4444' },
  { memberId: '3', memberName: 'Mom', memberColor: '#10b981' },
  { memberId: '4', memberName: 'Dad', memberColor: '#8b5cf6' },
  { memberId: '5', memberName: 'Sophia', memberColor: '#f59e0b' },
  { memberId: '6', memberName: 'Noah', memberColor: '#ec4899' },
  { memberId: '7', memberName: 'Olivia', memberColor: '#14b8a6' },
  { memberId: '8', memberName: 'Liam', memberColor: '#6366f1' },
  { memberId: '9', memberName: 'Ava', memberColor: '#f97316' },
  { memberId: '10', memberName: 'Mason', memberColor: '#8b5cf6' },
  { memberId: '11', memberName: 'Isabella', memberColor: '#06b6d4' },
  { memberId: '12', memberName: 'Ethan', memberColor: '#84cc16' },
  { memberId: '13', memberName: 'Mia', memberColor: '#ef4444' },
  { memberId: '14', memberName: 'James', memberColor: '#3b82f6' },
  { memberId: '15', memberName: 'Charlotte', memberColor: '#f59e0b' },
  { memberId: '16', memberName: 'Benjamin', memberColor: '#ec4899' },
  { memberId: '17', memberName: 'Amelia', memberColor: '#14b8a6' },
  { memberId: '18', memberName: 'William', memberColor: '#6366f1' },
  { memberId: '19', memberName: 'Harper', memberColor: '#f97316' },
  { memberId: '20', memberName: 'Alexander', memberColor: '#8b5cf6' },
];

// Mock lists with TodoList structure
const initialMockLists: TodoList[] = [
  { listId: '1', familyId: 'family-1', name: 'Shopping', theme: 'Groceries', icon: '🛒', createdAt: new Date(), updatedAt: new Date() },
  { listId: '2', familyId: 'family-1', name: 'Work', theme: 'Office', icon: '💼', createdAt: new Date(), updatedAt: new Date() },
  { listId: '3', familyId: 'family-1', name: 'Home', theme: 'Chores', icon: '🏠', createdAt: new Date(), updatedAt: new Date() },
  { listId: '4', familyId: 'family-1', name: 'Personal', theme: 'Self', icon: '👤', createdAt: new Date(), updatedAt: new Date() },
  { listId: '5', familyId: 'family-1', name: 'Health', theme: 'Fitness', icon: '💪', createdAt: new Date(), updatedAt: new Date() },
  { listId: '6', familyId: 'family-1', name: 'Travel', theme: 'Trips', icon: '✈️', createdAt: new Date(), updatedAt: new Date() },
  { listId: '7', familyId: 'family-1', name: 'Finance', theme: 'Money', icon: '💰', createdAt: new Date(), updatedAt: new Date() },
  { listId: '8', familyId: 'family-1', name: 'Education', theme: 'Learning', icon: '📚', createdAt: new Date(), updatedAt: new Date() },
  { listId: '9', familyId: 'family-1', name: 'Projects', theme: 'Work', icon: '🔨', createdAt: new Date(), updatedAt: new Date() },
  { listId: '10', familyId: 'family-1', name: 'Family', theme: 'Home', icon: '👨‍👩‍👧‍👦', createdAt: new Date(), updatedAt: new Date() },
];

// Generate many sample todos
const generateSampleTodos = (count: number): Todo[] => {
  const priorities: TodoPriority[] = ['low', 'medium', 'high', 'critical'];
  const statuses: TodoStatus[] = ['open', 'done'];
  const members = mockMembers;
  const listIds = ['1', '2', '3', '4', '5', null]; // Some todos have lists, some don't
  const mockEvents = [
    { eventId: 'event-1', eventTitle: 'Family Vacation' },
    { eventId: 'event-2', eventTitle: 'Birthday Party' },
    { eventId: 'event-3', eventTitle: 'School Play' },
    { eventId: 'event-4', eventTitle: 'Doctor Appointment' },
    { eventId: 'event-5', eventTitle: 'Soccer Game' },
    null, // Some todos don't have events
  ];
  
  return Array.from({ length: count }, (_, i) => {
    const priority = priorities[i % priorities.length];
    const status = i % 3 === 0 ? 'done' : 'open'; // 33% done
    const assignee = i % 5 === 0 ? undefined : members[i % members.length];
    const daysAgo = Math.floor(i / 10);
    const listId = listIds[i % listIds.length]; // Distribute todos across lists
    const event = mockEvents[i % mockEvents.length]; // Some todos linked to events
    
    return {
      todoId: `todo-${i}`,
      familyId: 'family-1',
      title: `Todo item ${i + 1}: ${['Buy groceries', 'Call dentist', 'Finish homework', 'Plan activity', 'Pick up dry cleaning', 'Schedule meeting', 'Review documents', 'Send email'][i % 8]}`,
      description: i % 4 === 0 ? `Description for todo ${i + 1}` : undefined,
      status,
      priority,
      dueDateTime: new Date(Date.now() - daysAgo * 86400000 + (i % 3) * 86400000),
      assigneeId: assignee?.memberId,
      assigneeName: assignee?.memberName,
      isFamily: !assignee,
      listId: listId || undefined,
      eventId: event?.eventId,
      eventTitle: event?.eventTitle,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  });
};

const manyTodos = generateSampleTodos(120);

// Proposal 2: Compact List with Virtual Scrolling Indicator + Lists
function Proposal2Compact({ isDarkMode }: { isDarkMode: boolean }) {
  const tokens = getDesignTokens(isDarkMode);
  const [todos, setTodos] = useState<Todo[]>(manyTodos);
  const [lists, setLists] = useState<TodoList[]>(initialMockLists);
  const [visibleCount, setVisibleCount] = useState(30);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<TodoPriority | 'all'>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string | 'all'>('all');
  const [listFilter, setListFilter] = useState<string | 'all'>('all');
  const [showLists, setShowLists] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{
    title?: string;
    description?: string;
    priority?: TodoPriority;
    status?: TodoStatus;
    assigneeId?: string;
    assigneeName?: string;
    listId?: string;
    dueDateTime?: Date | string;
    eventId?: string;
    eventTitle?: string;
  }>({});
  const [listSearchQuery, setListSearchQuery] = useState('');
  const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');
  const [swipeOffsets, setSwipeOffsets] = useState<Record<string, number>>({});
  const [swipeStartX, setSwipeStartX] = useState<Record<string, number>>({});
  const [isSwiping, setIsSwiping] = useState<Record<string, boolean>>({});
  
  // List management state
  const [showListManagement, setShowListManagement] = useState(false);
  const [showCreateListSheet, setShowCreateListSheet] = useState(false);
  const [showEditListSheet, setShowEditListSheet] = useState(false);
  const [editingList, setEditingList] = useState<TodoList | null>(null);
  const [newListName, setNewListName] = useState('');
  const [newListIcon, setNewListIcon] = useState('');
  const [editListName, setEditListName] = useState('');
  const [editListIcon, setEditListIcon] = useState('');

  // Mock lists with todo counts
  const listsWithCounts = lists.map(list => ({
    ...list,
    count: todos.filter(t => t.listId === list.listId).length,
  }));

  // List management functions
  const createList = (name: string, icon?: string) => {
    const newList: TodoList = {
      listId: `list-${Date.now()}`,
      familyId: 'family-1',
      name: name.trim(),
      icon: icon || '📋',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setLists(prev => [...prev, newList]);
    setNewListName('');
    setNewListIcon('');
    setShowCreateListSheet(false);
  };

  const updateList = (listId: string, updates: { name?: string; icon?: string }) => {
    setLists(prev =>
      prev.map(list =>
        list.listId === listId
          ? { ...list, ...updates, updatedAt: new Date() }
          : list
      )
    );
    setShowEditListSheet(false);
    setEditingList(null);
    setEditListName('');
    setEditListIcon('');
  };

  const deleteList = (listId: string) => {
    if (window.confirm('Are you sure you want to delete this list? Todos in this list will not be deleted.')) {
      setLists(prev => prev.filter(list => list.listId !== listId));
      // Remove listId from todos in this list
      setTodos(prev =>
        prev.map(todo =>
          todo.listId === listId ? { ...todo, listId: undefined } : todo
        )
      );
    }
  };

  const filtered = todos.filter(t => {
    if (searchQuery && !t.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
    if (assigneeFilter !== 'all' && t.assigneeId !== assigneeFilter) return false;
    if (listFilter !== 'all' && t.listId !== listFilter) return false;
    return true;
  });

  const visibleTodos = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  // Swipe handlers
  const handleTouchStart = (todoId: string, e: React.TouchEvent) => {
    const touch = e.touches[0];
    setSwipeStartX(prev => ({ ...prev, [todoId]: touch.clientX }));
    setIsSwiping(prev => ({ ...prev, [todoId]: true }));
  };

  const handleTouchMove = (todoId: string, e: React.TouchEvent) => {
    if (!isSwiping[todoId]) return;
    const touch = e.touches[0];
    const startX = swipeStartX[todoId] || 0;
    const deltaX = touch.clientX - startX;
    setSwipeOffsets(prev => ({ ...prev, [todoId]: deltaX }));
  };

  const handleTouchEnd = (todoId: string, e?: React.TouchEvent) => {
    const offset = swipeOffsets[todoId] || 0;
    const todo = todos.find(t => t.todoId === todoId);
    
    if (Math.abs(offset) > 100 && todo) {
      // Swipe right to mark done, swipe left to mark open
      if (offset > 0 && todo.status === 'open') {
        updateTodo(todoId, { status: 'done' });
      } else if (offset < 0 && todo.status === 'done') {
        updateTodo(todoId, { status: 'open' });
      }
    }
    
    setSwipeOffsets(prev => {
      const newOffsets = { ...prev };
      delete newOffsets[todoId];
      return newOffsets;
    });
    setIsSwiping(prev => {
      const newSwiping = { ...prev };
      delete newSwiping[todoId];
      return newSwiping;
    });
    setSwipeStartX(prev => {
      const newStartX = { ...prev };
      delete newStartX[todoId];
      return newStartX;
    });
  };

  // Update todo function
  const updateTodo = (todoId: string, updates: Partial<Todo>) => {
    setTodos(prev =>
      prev.map(todo =>
        todo.todoId === todoId ? { ...todo, ...updates } : todo
      )
    );
  };

  // Delete todo function
  const deleteTodo = (todoId: string) => {
    if (window.confirm('Are you sure you want to delete this todo?')) {
      setTodos(prev => prev.filter(todo => todo.todoId !== todoId));
      setShowDetailSheet(false);
      setSelectedTodo(null);
    }
  };

  // Confirm field edit function
  const confirmFieldEdit = (field: string) => {
    if (!selectedTodo) return;
    
    const updates: Partial<Todo> = {};
    
    if (field === 'title' && editValues.title !== undefined) {
      updates.title = editValues.title;
    } else if (field === 'description' && editValues.description !== undefined) {
      updates.description = editValues.description;
    } else if (field === 'priority' && editValues.priority) {
      updates.priority = editValues.priority;
    } else if (field === 'status' && editValues.status) {
      updates.status = editValues.status;
    } else if (field === 'assignee' && editValues.assigneeId !== undefined) {
      updates.assigneeId = editValues.assigneeId;
      updates.assigneeName = editValues.assigneeName;
    } else if (field === 'list' && editValues.listId !== undefined) {
      updates.listId = editValues.listId;
    } else if (field === 'dueDate' && editValues.dueDateTime !== undefined) {
      updates.dueDateTime = editValues.dueDateTime;
    } else if (field === 'event' && editValues.eventId !== undefined) {
      updates.eventId = editValues.eventId;
      updates.eventTitle = editValues.eventTitle;
    }
    
    updateTodo(selectedTodo.todoId, updates);
    setEditingField(null);
    setEditValues({});
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* Compact Header */}
        <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-10`}>
          <div className="px-4 py-2">
            <div className="flex items-center justify-between mb-2">
              <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Todos & Lists</h1>
              <span className={`text-sm ${tokens.text.muted}`}>{filtered.length} total</span>
            </div>

          {/* Search */}
          <input
            type="text"
            className={`w-full px-3 py-2 rounded-landing text-sm ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[40px] mb-2`}
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* Lists Section - Expandable */}
          <div className="mb-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowLists(!showLists)}
                className={`flex-1 flex items-center justify-between px-2 py-1.5 rounded-landing ${tokens.button.secondary} min-h-[36px]`}
              >
                <div className="flex items-center gap-2">
                  <Icon name="checklist" size="sm" />
                  <span className={`text-xs font-semibold ${tokens.text.primary}`}>
                    Lists {listFilter !== 'all' && `(${listsWithCounts.find(l => l.listId === listFilter)?.name})`}
                  </span>
                </div>
                <Icon name={showLists ? 'chevron-up' : 'chevron-down'} size="sm" />
              </button>
              <button
                onClick={() => setShowListManagement(true)}
                className={`px-2 py-1.5 rounded-landing ${tokens.button.secondary} min-h-[36px] flex items-center justify-center gap-1`}
                title="Manage Lists - Create, edit, or delete lists"
                aria-label="Manage Lists"
              >
                <Icon name="settings" size="sm" />
                <span className="text-xs font-medium">Manage</span>
              </button>
            </div>
            {showLists && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  className={`p-3 rounded-landing border-2 min-h-[60px] flex flex-col items-center justify-center gap-1 ${
                    listFilter === 'all'
                      ? `${tokens.border.accent} ${tokens.button.secondary}`
                      : `${tokens.border.light} ${tokens.card.bg}`
                  }`}
                  onClick={() => setListFilter('all')}
                >
                  <span className="text-lg">📋</span>
                  <span className={`text-xs font-semibold ${tokens.text.primary}`}>All Lists</span>
                  <span className={`text-[10px] ${tokens.text.muted}`}>{todos.length} todos</span>
                </button>
                {listsWithCounts.map((list) => (
                  <button
                    key={list.listId}
                    className={`p-3 rounded-landing border-2 min-h-[60px] flex flex-col items-center justify-center gap-1 ${
                      listFilter === list.listId
                        ? `${tokens.border.accent} ${tokens.button.secondary}`
                        : `${tokens.border.light} ${tokens.card.bg}`
                    }`}
                    onClick={() => setListFilter(list.listId)}
                  >
                    <span className="text-lg">{list.icon}</span>
                    <span className={`text-xs font-semibold ${tokens.text.primary} text-center`}>{list.name}</span>
                    <span className={`text-[10px] ${tokens.text.muted}`}>{list.count} todos</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick Filters */}
          <div className="space-y-2">
            {/* Priority Filters */}
            <div className="flex gap-2 flex-wrap items-center">
              <span className={`text-xs font-medium ${tokens.text.muted} mr-1`}>Priority:</span>
              {(['all', 'critical', 'high', 'medium', 'low'] as const).map((filter) => {
                const isSelected = priorityFilter === filter;
                const priorityColors = {
                  all: { bg: isSelected ? tokens.button.secondary : tokens.card.bg, text: tokens.text.primary, border: tokens.border.light },
                  critical: { bg: isSelected ? 'bg-red-100' : 'bg-red-50', text: 'text-red-700', border: 'border-red-300' },
                  high: { bg: isSelected ? 'bg-orange-100' : 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300' },
                  medium: { bg: isSelected ? 'bg-yellow-100' : 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-300' },
                  low: { bg: isSelected ? 'bg-blue-100' : 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300' },
                };
                const colors = priorityColors[filter];
                return (
                  <button
                    key={filter}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold min-h-[32px] flex items-center gap-1.5 border-2 ${
                      filter === 'all' 
                        ? `${isSelected ? tokens.button.secondary : tokens.card.bg} ${tokens.text.primary} ${tokens.border.light}`
                        : `${colors.bg} ${colors.text} ${colors.border} ${isSelected ? 'ring-2 ring-offset-1' : ''}`
                    }`}
                    onClick={() => setPriorityFilter(filter)}
                  >
                    {filter === 'all' ? (
                      <>
                        <span>All</span>
                      </>
                    ) : (
                      <>
                        <span className={`w-2 h-2 rounded-full ${
                          filter === 'critical' ? 'bg-red-500' :
                          filter === 'high' ? 'bg-orange-500' :
                          filter === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                        }`}></span>
                        <span className="capitalize">{filter}</span>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Assignee Filters */}
            <div className="flex gap-2 flex-wrap items-center">
              <span className={`text-xs font-medium ${tokens.text.muted} mr-1`}>Assigned to:</span>
              <button
                className={`px-3 py-1.5 rounded-full text-xs font-semibold min-h-[32px] flex items-center gap-1.5 border-2 ${
                  assigneeFilter === 'all' 
                    ? `${tokens.button.secondary} ${tokens.text.primary} ${tokens.border.light}`
                    : `${tokens.card.bg} ${tokens.text.secondary} ${tokens.border.light}`
                }`}
                onClick={() => setAssigneeFilter('all')}
              >
                <span>All</span>
              </button>
              {mockMembers.map((member) => (
                <button
                  key={member.memberId}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold min-h-[32px] flex items-center gap-2 border-2 ${
                    assigneeFilter === member.memberId 
                      ? `${tokens.button.secondary} ${tokens.text.primary} ${tokens.border.accent} ring-2 ring-offset-1`
                      : `${tokens.card.bg} ${tokens.text.secondary} ${tokens.border.light}`
                  }`}
                  onClick={() => setAssigneeFilter(member.memberId)}
                >
                  <MemberAvatar member={member} size="sm" />
                  <span>{member.memberName}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Compact List */}
      <div className="px-3 py-2">
        <div className="space-y-1">
          {visibleTodos.map(todo => {
            const swipeOffset = swipeOffsets[todo.todoId] || 0;
            const swipePercentage = Math.abs(swipeOffset) / 100;
            const isSwipingRight = swipeOffset > 0;
            const isSwipingLeft = swipeOffset < 0;
            const isCurrentlySwiping = isSwiping[todo.todoId];

            return (
              <div key={todo.todoId} className="relative mb-1 overflow-hidden">
                {/* Background action indicator */}
                {(isSwipingRight || isSwipingLeft) && (
                  <div 
                    className={`absolute inset-0 ${
                      isSwipingRight ? tokens.button.primary : 'bg-gray-400'
                    }`}
                    style={{ 
                      opacity: Math.max(0.7, swipePercentage),
                      zIndex: 0
                    }}
                  >
                    <div className={`flex items-center h-full ${
                      isSwipingRight ? 'justify-start pl-4' : 'justify-end pr-4'
                    }`}>
                      {isSwipingRight && (
                        <Icon name="check" size="md" className="text-white" />
                      )}
                      {isSwipingLeft && (
                        <Icon name="arrow-left" size="md" className="text-white" />
                      )}
                    </div>
                  </div>
                )}

                {/* Todo card */}
                <div
                  onClick={() => {
                    if (!isCurrentlySwiping) {
                      setSelectedTodo(todo);
                      setShowDetailSheet(true);
                      setEditingField(null);
                      setEditValues({});
                    }
                  }}
                  onTouchStart={(e) => handleTouchStart(todo.todoId, e)}
                  onTouchMove={(e) => handleTouchMove(todo.todoId, e)}
                  onTouchEnd={(e) => handleTouchEnd(todo.todoId, e)}
                  className={`${tokens.card.bg} ${tokens.border.light} border-l-4 rounded-landing p-2 min-h-[44px] flex items-center gap-2 cursor-pointer touch-manipulation transition-transform relative z-10`}
                  style={{
                    borderLeftColor: todo.priority === 'critical' ? '#ef4444' :
                                     todo.priority === 'high' ? '#f97316' :
                                     todo.priority === 'medium' ? '#eab308' : '#3b82f6',
                    opacity: todo.status === 'done' ? 0.6 : 1,
                    transform: `translateX(${swipeOffset}px)`,
                  }}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    todo.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}>
                    {todo.status === 'done' && <Icon name="check" size="sm" className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-xs font-semibold truncate ${
                      todo.status === 'done' ? 'line-through text-gray-500' : tokens.text.primary
                    }`}>
                      {todo.title}
                    </h3>
                  </div>
                  {todo.assigneeName && (
                    <MemberAvatar
                      member={mockMembers.find(m => m.memberName === todo.assigneeName) || mockMembers[0]}
                      size="sm"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Load More */}
        {hasMore && (
          <button
            onClick={() => setVisibleCount(prev => prev + 30)}
            className={`w-full mt-4 py-3 rounded-landing ${tokens.button.secondary} font-semibold min-h-[48px]`}
          >
            Load More ({filtered.length - visibleCount} remaining)
          </button>
        )}
      </div>

      {/* Todo Detail Bottom Sheet */}
      <BottomSheet
        isOpen={showDetailSheet}
        onClose={() => {
          setShowDetailSheet(false);
          setSelectedTodo(null);
          setEditingField(null);
          setEditValues({});
          setListSearchQuery('');
          setAssigneeSearchQuery('');
        }}
        title=""
      >
        {selectedTodo && (
          <div className="p-4">
            {/* Title - Large and prominent */}
            <div className="mb-6 relative">
              {editingField === 'title' ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    autoFocus
                    className={`flex-1 text-2xl font-bold ${tokens.text.primary} ${tokens.card.bg} border-b-2 ${tokens.border.accent} pb-2 focus:outline-none`}
                    value={editValues.title !== undefined ? editValues.title : selectedTodo.title}
                    onChange={(e) => setEditValues({ ...editValues, title: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        confirmFieldEdit('title');
                      }
                    }}
                  />
                  <button
                    onClick={() => confirmFieldEdit('title')}
                    className={`w-10 h-10 rounded-full ${tokens.button.primary} flex items-center justify-center touch-manipulation active:scale-95 transition-transform`}
                  >
                    <Icon name="check" size="md" className="text-white" />
                  </button>
                </div>
              ) : (
                <h1
                  onClick={() => {
                    setEditingField('title');
                    setEditValues({ title: selectedTodo.title });
                  }}
                  className={`text-2xl font-bold ${tokens.text.primary} pb-2 cursor-pointer touch-manipulation active:opacity-70 ${
                    selectedTodo.status === 'done' ? 'line-through opacity-60' : ''
                  }`}
                >
                  {selectedTodo.title}
                </h1>
              )}
            </div>

            {/* Description */}
            {selectedTodo.description && (
              <div
                onClick={() => !editingField && setEditingField('description')}
                className={`mb-4 p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border relative ${
                  editingField === 'description' ? 'ring-2 ring-blue-500' : 'cursor-pointer touch-manipulation active:opacity-70'
                }`}
              >
                {editingField === 'description' ? (
                  <>
                    <textarea
                      autoFocus
                      className={`w-full pr-12 ${tokens.text.primary} ${tokens.card.bg} focus:outline-none resize-none`}
                      value={selectedTodo.description}
                      rows={3}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingField(null);
                      }}
                      className={`absolute top-3 right-3 w-8 h-8 rounded-full ${tokens.button.primary} flex items-center justify-center touch-manipulation active:scale-95 transition-transform`}
                    >
                      <Icon name="check" size="sm" className="text-white" />
                    </button>
                  </>
                ) : (
                  <p className={`text-sm ${tokens.text.secondary} whitespace-pre-wrap`}>
                    {selectedTodo.description}
                  </p>
                )}
              </div>
            )}

            {/* Info Tiles Grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {/* Priority Tile */}
              <div
                onClick={() => !editingField && setEditingField('priority')}
                className={`p-4 rounded-landing ${tokens.card.bg} ${tokens.border.light} border relative ${
                  editingField === 'priority' ? 'ring-2 ring-blue-500' : 'cursor-pointer touch-manipulation active:scale-95 transition-transform'
                } flex flex-col items-center justify-center min-h-[100px]`}
                style={{
                  borderLeftWidth: '4px',
                  borderLeftColor: (editValues.priority !== undefined ? editValues.priority : selectedTodo.priority) === 'critical' ? '#ef4444' :
                                   (editValues.priority !== undefined ? editValues.priority : selectedTodo.priority) === 'high' ? '#f97316' :
                                   (editValues.priority !== undefined ? editValues.priority : selectedTodo.priority) === 'medium' ? '#eab308' : '#3b82f6',
                }}
              >
                {editingField === 'priority' ? (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmFieldEdit('priority');
                      }}
                      className={`absolute top-2 right-2 w-8 h-8 rounded-full ${tokens.button.primary} flex items-center justify-center touch-manipulation active:scale-95 transition-transform shadow-md`}
                    >
                      <Icon name="check" size="sm" className="text-white" />
                    </button>
                    <div className="flex flex-col gap-2 mt-2">
                      {(['low', 'medium', 'high', 'critical'] as TodoPriority[]).map(priority => (
                        <button
                          key={priority}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditValues({ ...editValues, priority });
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                            (editValues.priority !== undefined ? editValues.priority : selectedTodo.priority) === priority
                              ? priority === 'critical' ? 'bg-red-100 text-red-700 ring-2 ring-red-500' :
                                priority === 'high' ? 'bg-orange-100 text-orange-700 ring-2 ring-orange-500' :
                                priority === 'medium' ? 'bg-yellow-100 text-yellow-700 ring-2 ring-yellow-500' :
                                'bg-blue-100 text-blue-700 ring-2 ring-blue-500'
                              : priority === 'critical' ? 'bg-red-50 text-red-600' :
                                priority === 'high' ? 'bg-orange-50 text-orange-600' :
                                priority === 'medium' ? 'bg-yellow-50 text-yellow-600' :
                                'bg-blue-50 text-blue-600'
                          }`}
                        >
                          {priority}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
                <Icon name="warning" size="md" className={`mb-2 ${
                  (editValues.priority !== undefined ? editValues.priority : selectedTodo.priority) === 'critical' ? 'text-red-500' :
                  (editValues.priority !== undefined ? editValues.priority : selectedTodo.priority) === 'high' ? 'text-orange-500' :
                  (editValues.priority !== undefined ? editValues.priority : selectedTodo.priority) === 'medium' ? 'text-yellow-500' : 'text-blue-500'
                }`} />
                <span className={`text-xs font-medium ${tokens.text.muted} mb-1`}>Priority</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  (editValues.priority !== undefined ? editValues.priority : selectedTodo.priority) === 'critical' ? 'bg-red-100 text-red-700' :
                  (editValues.priority !== undefined ? editValues.priority : selectedTodo.priority) === 'high' ? 'bg-orange-100 text-orange-700' :
                  (editValues.priority !== undefined ? editValues.priority : selectedTodo.priority) === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {(editValues.priority !== undefined ? editValues.priority : selectedTodo.priority) || 'medium'}
                </span>
              </div>

              {/* Status Tile */}
              <div
                onClick={() => !editingField && setEditingField('status')}
                className={`p-4 rounded-landing ${tokens.card.bg} ${tokens.border.light} border relative ${
                  editingField === 'status' ? 'ring-2 ring-blue-500' : 'cursor-pointer touch-manipulation active:scale-95 transition-transform'
                } flex flex-col items-center justify-center min-h-[100px]`}
                style={{
                  borderLeftWidth: '4px',
                  borderLeftColor: (editValues.status !== undefined ? editValues.status : selectedTodo.status) === 'done' ? '#10b981' : '#3b82f6',
                }}
              >
                {editingField === 'status' ? (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmFieldEdit('status');
                      }}
                      className={`absolute top-2 right-2 w-8 h-8 rounded-full ${tokens.button.primary} flex items-center justify-center touch-manipulation active:scale-95 transition-transform shadow-md`}
                    >
                      <Icon name="check" size="sm" className="text-white" />
                    </button>
                    <div className="flex gap-2 mt-2">
                      {(['open', 'done'] as TodoStatus[]).map(status => (
                        <button
                          key={status}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditValues({ ...editValues, status });
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                            (editValues.status !== undefined ? editValues.status : selectedTodo.status) === status
                              ? status === 'done' ? 'bg-green-100 text-green-700 ring-2 ring-green-500' : 'bg-blue-100 text-blue-700 ring-2 ring-blue-500'
                              : status === 'done' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'
                          }`}
                        >
                          {status === 'done' ? 'Done' : 'Open'}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
                <Icon name="check" size="md" className={`mb-2 ${
                  (editValues.status !== undefined ? editValues.status : selectedTodo.status) === 'done' ? 'text-green-500' : 'text-blue-500'
                }`} />
                <span className={`text-xs font-medium ${tokens.text.muted} mb-1`}>Status</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  (editValues.status !== undefined ? editValues.status : selectedTodo.status) === 'done' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {(editValues.status !== undefined ? editValues.status : selectedTodo.status) === 'done' ? 'Done' : 'Open'}
                </span>
              </div>

              {/* Due Date Tile */}
              {selectedTodo.dueDateTime && (
                <div
                  onClick={() => !editingField && setEditingField('dueDate')}
                  className={`p-4 rounded-landing ${tokens.card.bg} ${tokens.border.light} border relative ${
                    editingField === 'dueDate' ? 'ring-2 ring-blue-500' : 'cursor-pointer touch-manipulation active:scale-95 transition-transform'
                  } flex flex-col items-center justify-center min-h-[100px]`}
                >
                  {editingField === 'dueDate' ? (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmFieldEdit('dueDate');
                        }}
                        className={`absolute top-2 right-2 w-8 h-8 rounded-full ${tokens.button.primary} flex items-center justify-center touch-manipulation active:scale-95 transition-transform shadow-md`}
                      >
                        <Icon name="check" size="sm" className="text-white" />
                      </button>
                      <input
                        type="date"
                        className="mt-2 w-full px-2 py-1 rounded text-sm border"
                        value={editValues.dueDateTime ? (typeof editValues.dueDateTime === 'string' && editValues.dueDateTime.includes('T') 
                          ? editValues.dueDateTime.split('T')[0] 
                          : new Date(editValues.dueDateTime).toISOString().split('T')[0]) 
                          : selectedTodo.dueDateTime 
                            ? (typeof selectedTodo.dueDateTime === 'string' && selectedTodo.dueDateTime.includes('T')
                              ? selectedTodo.dueDateTime.split('T')[0]
                              : new Date(selectedTodo.dueDateTime).toISOString().split('T')[0])
                            : ''}
                        onChange={(e) => setEditValues({ ...editValues, dueDateTime: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </>
                  ) : null}
                  <Icon name="clock" size="md" className={`mb-2 ${tokens.text.accent}`} />
                  <span className={`text-xs font-medium ${tokens.text.muted} mb-1`}>Due Date</span>
                  <span className={`text-sm font-semibold ${tokens.text.primary}`}>Today</span>
                </div>
              )}

              {/* Assignee Tile */}
              <div
                onClick={() => !editingField && setEditingField('assignee')}
                className={`p-4 rounded-landing ${tokens.card.bg} ${tokens.border.light} border relative ${
                  editingField === 'assignee' ? 'ring-2 ring-blue-500' : 'cursor-pointer touch-manipulation active:scale-95 transition-transform'
                } flex flex-col items-center justify-center min-h-[100px]`}
              >
                {editingField === 'assignee' ? (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmFieldEdit('assignee');
                        setAssigneeSearchQuery('');
                      }}
                      className={`absolute top-2 right-2 w-8 h-8 rounded-full ${tokens.button.primary} flex items-center justify-center touch-manipulation active:scale-95 transition-transform shadow-md z-10`}
                    >
                      <Icon name="check" size="sm" className="text-white" />
                    </button>
                    <div className="mt-2 space-y-2">
                      {/* Search for assignees */}
                      <input
                        type="text"
                        placeholder="Search people..."
                        value={assigneeSearchQuery}
                        onChange={(e) => setAssigneeSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-full px-3 py-2 rounded-landing text-sm ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[40px] focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        autoFocus
                      />
                      {/* Scrollable list */}
                      <div className="max-h-48 overflow-y-auto space-y-1 pr-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditValues({ ...editValues, assigneeId: 'family', assigneeName: undefined });
                          }}
                          className={`w-full px-3 py-2 rounded-landing text-sm font-medium flex items-center gap-2 min-h-[44px] text-left ${
                            (editValues.assigneeId !== undefined ? editValues.assigneeId : selectedTodo.assigneeId || 'family') === 'family'
                              ? `${tokens.button.primary} text-white` : `${tokens.button.secondary}`
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-500 text-white text-sm font-bold">
                            👥
                          </div>
                          <span>Family</span>
                          {(editValues.assigneeId !== undefined ? editValues.assigneeId : selectedTodo.assigneeId || 'family') === 'family' && (
                            <Icon name="check" size="sm" className="ml-auto" />
                          )}
                        </button>
                        {mockMembers
                          .filter(member => 
                            !assigneeSearchQuery || 
                            member.memberName.toLowerCase().includes(assigneeSearchQuery.toLowerCase())
                          )
                          .map(member => (
                            <button
                              key={member.memberId}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditValues({ ...editValues, assigneeId: member.memberId, assigneeName: member.memberName });
                              }}
                              className={`w-full px-3 py-2 rounded-landing text-sm font-medium flex items-center gap-2 min-h-[44px] text-left ${
                                (editValues.assigneeId !== undefined ? editValues.assigneeId : selectedTodo.assigneeId) === member.memberId
                                  ? `${tokens.button.primary} text-white` : `${tokens.button.secondary}`
                              }`}
                            >
                              <MemberAvatar member={member} size="sm" />
                              <span className="flex-1">{member.memberName}</span>
                              {(editValues.assigneeId !== undefined ? editValues.assigneeId : selectedTodo.assigneeId) === member.memberId && (
                                <Icon name="check" size="sm" className="ml-auto" />
                              )}
                            </button>
                          ))}
                        {mockMembers.filter(member => 
                          assigneeSearchQuery && 
                          !member.memberName.toLowerCase().includes(assigneeSearchQuery.toLowerCase())
                        ).length > 0 && (
                          <div className={`text-xs ${tokens.text.muted} text-center py-2`}>
                            No people found matching "{assigneeSearchQuery}"
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : null}
                {(editValues.assigneeName !== undefined ? editValues.assigneeName : selectedTodo.assigneeName) ? (
                  <>
                    <MemberAvatar
                      member={mockMembers.find(m => m.memberName === (editValues.assigneeName !== undefined ? editValues.assigneeName : selectedTodo.assigneeName)) || mockMembers[0]}
                      size="md"
                      className="mb-2"
                    />
                    <span className={`text-xs font-medium ${tokens.text.muted} mb-1`}>Assigned To</span>
                    <span className={`text-sm font-semibold ${tokens.text.primary} text-center`}>
                      {editValues.assigneeName !== undefined ? editValues.assigneeName : selectedTodo.assigneeName}
                    </span>
                  </>
                ) : (
                  <>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-blue-500 text-white text-xl font-bold mb-2`}>
                      👥
                    </div>
                    <span className={`text-xs font-medium ${tokens.text.muted} mb-1`}>Assigned To</span>
                    <span className={`text-sm font-semibold ${tokens.text.primary}`}>Family</span>
                  </>
                )}
              </div>

              {/* List Tile */}
              {selectedTodo.listId && (
                <div
                  onClick={() => !editingField && setEditingField('list')}
                  className={`p-4 rounded-landing ${tokens.card.bg} ${tokens.border.light} border relative ${
                    editingField === 'list' ? 'ring-2 ring-blue-500' : 'cursor-pointer touch-manipulation active:scale-95 transition-transform'
                  } flex flex-col items-center justify-center min-h-[100px]`}
                >
                  {editingField === 'list' ? (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmFieldEdit('list');
                          setListSearchQuery('');
                        }}
                        className={`absolute top-2 right-2 w-8 h-8 rounded-full ${tokens.button.primary} flex items-center justify-center touch-manipulation active:scale-95 transition-transform shadow-md z-10`}
                      >
                        <Icon name="check" size="sm" className="text-white" />
                      </button>
                      <div className="mt-2 space-y-2">
                        {/* Search for lists */}
                        <input
                          type="text"
                          placeholder="Search lists..."
                          value={listSearchQuery}
                          onChange={(e) => setListSearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className={`w-full px-3 py-2 rounded-landing text-sm ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[40px] focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          autoFocus
                        />
                        {/* Scrollable list */}
                        <div className="max-h-48 overflow-y-auto space-y-1 pr-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditValues({ ...editValues, listId: undefined });
                            }}
                            className={`w-full px-3 py-2 rounded-landing text-sm font-medium flex items-center gap-2 min-h-[44px] text-left ${
                              !(editValues.listId !== undefined ? editValues.listId : selectedTodo.listId)
                                ? `${tokens.button.primary} text-white` : `${tokens.button.secondary}`
                            }`}
                          >
                            <span className="text-lg">📋</span>
                            <span>No List</span>
                            {!(editValues.listId !== undefined ? editValues.listId : selectedTodo.listId) && (
                              <Icon name="check" size="sm" className="ml-auto" />
                            )}
                          </button>
                          {lists
                            .filter(list => 
                              !listSearchQuery || 
                              list.name.toLowerCase().includes(listSearchQuery.toLowerCase())
                            )
                            .map(list => (
                              <button
                                key={list.listId}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditValues({ ...editValues, listId: list.listId });
                                }}
                                className={`w-full px-3 py-2 rounded-landing text-sm font-medium flex items-center gap-2 min-h-[44px] text-left ${
                                  (editValues.listId !== undefined ? editValues.listId : selectedTodo.listId) === list.listId
                                    ? `${tokens.button.primary} text-white` : `${tokens.button.secondary}`
                                }`}
                              >
                                <span className="text-lg">{list.icon}</span>
                                <span className="flex-1">{list.name}</span>
                                {(editValues.listId !== undefined ? editValues.listId : selectedTodo.listId) === list.listId && (
                                  <Icon name="check" size="sm" className="ml-auto" />
                                )}
                              </button>
                            ))}
                          {lists.filter(list => 
                            listSearchQuery && 
                            !list.name.toLowerCase().includes(listSearchQuery.toLowerCase())
                          ).length > 0 && (
                            <div className={`text-xs ${tokens.text.muted} text-center py-2`}>
                              No lists found matching "{listSearchQuery}"
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : null}
                  <div className={`text-3xl mb-2`}>
                    {lists.find(l => l.listId === selectedTodo.listId)?.icon || '📋'}
                  </div>
                  <span className={`text-xs font-medium ${tokens.text.muted} mb-1`}>List</span>
                  <span className={`text-sm font-semibold ${tokens.text.primary} text-center`}>
                    {lists.find(l => l.listId === (editValues.listId !== undefined ? editValues.listId : selectedTodo.listId))?.name || 'Unknown List'}
                  </span>
                </div>
              )}

              {/* Linked Event Tile - Full Width */}
              {selectedTodo.eventId && selectedTodo.eventTitle && (
                <div
                  onClick={() => !editingField && setEditingField('event')}
                  className={`col-span-2 p-4 rounded-landing ${tokens.card.bg} ${tokens.border.accent} border-2 relative ${
                    editingField === 'event' ? 'ring-2 ring-blue-500' : 'cursor-pointer touch-manipulation active:scale-95 transition-transform'
                  } flex items-center gap-3 min-h-[80px]`}
                >
                  {editingField === 'event' ? (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmFieldEdit('event');
                        }}
                        className={`absolute top-2 right-2 w-8 h-8 rounded-full ${tokens.button.primary} flex items-center justify-center touch-manipulation active:scale-95 transition-transform shadow-md`}
                      >
                        <Icon name="check" size="sm" className="text-white" />
                      </button>
                      <div className="mt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditValues({ ...editValues, eventId: undefined, eventTitle: undefined });
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                            !(editValues.eventId !== undefined ? editValues.eventId : selectedTodo.eventId)
                              ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-500' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          Remove Link
                        </button>
                      </div>
                    </>
                  ) : null}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    tokens.button.primary
                  }`}>
                    <Icon name="calendar" size="md" className="text-white" />
                  </div>
                  <div className="flex-1">
                    <span className={`text-xs font-medium ${tokens.text.muted} block mb-1`}>Linked Event</span>
                    <span className={`text-base font-semibold ${tokens.text.primary}`}>{selectedTodo.eventTitle}</span>
                  </div>
                  <span className={`text-xs px-3 py-1.5 rounded-full ${tokens.button.secondary} font-medium`}>
                    Linked
                  </span>
                </div>
              )}
            </div>

            {/* Delete Button - Bottom */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  if (selectedTodo && window.confirm('Are you sure you want to delete this todo?')) {
                    deleteTodo(selectedTodo.todoId);
                  }
                }}
                className={`w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-landing active:scale-95 min-h-[48px] flex items-center justify-center gap-2 transition-all`}
              >
                <Icon name="delete" size="md" />
                <span>Delete Todo</span>
              </button>
            </div>
          </div>
        )}
      </BottomSheet>

      {/* List Management Bottom Sheet */}
      <BottomSheet
        isOpen={showListManagement}
        onClose={() => {
          setShowListManagement(false);
        }}
        title="Manage Lists"
      >
        <div className="p-4 space-y-4">
          <button
            onClick={() => {
              setShowListManagement(false);
              setShowCreateListSheet(true);
            }}
            className={`w-full px-4 py-3 ${tokens.button.primary} font-semibold rounded-landing min-h-[48px] flex items-center justify-center gap-2`}
          >
            <Icon name="plus" size="sm" />
            <span>Create New List</span>
          </button>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {lists.map((list) => {
              const count = todos.filter(t => t.listId === list.listId).length;
              return (
                <div
                  key={list.listId}
                  className={`p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border flex items-center justify-between`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{list.icon}</span>
                    <div>
                      <div className={`text-sm font-semibold ${tokens.text.primary}`}>{list.name}</div>
                      <div className={`text-xs ${tokens.text.muted}`}>{count} todos</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setEditingList(list);
                        setEditListName(list.name);
                        setEditListIcon(list.icon || '');
                        setShowListManagement(false);
                        setShowEditListSheet(true);
                      }}
                      className={`px-3 py-1.5 rounded-landing ${tokens.button.secondary} min-h-[36px] flex items-center justify-center`}
                    >
                      <Icon name="edit" size="sm" />
                    </button>
                    <button
                      onClick={() => deleteList(list.listId)}
                      className={`px-3 py-1.5 rounded-landing bg-red-600 text-white min-h-[36px] flex items-center justify-center`}
                    >
                      <Icon name="delete" size="sm" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </BottomSheet>

      {/* Create List Bottom Sheet */}
      <BottomSheet
        isOpen={showCreateListSheet}
        onClose={() => {
          setShowCreateListSheet(false);
          setNewListName('');
          setNewListIcon('');
        }}
        title="Create New List"
      >
        <div className="p-4 space-y-4">
          <div>
            <label className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
              List Name *
            </label>
            <input
              type="text"
              className={`w-full px-3 py-2.5 ${tokens.border.light} border rounded-landing text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="e.g., Shopping, Work, Home"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              autoFocus
            />
          </div>

          <div>
            <label className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
              Icon (optional)
            </label>
            <input
              type="text"
              className={`w-full px-3 py-2.5 ${tokens.border.light} border rounded-landing text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="e.g., 🛒, 💼, 🏠"
              value={newListIcon}
              onChange={(e) => setNewListIcon(e.target.value)}
              maxLength={2}
            />
            <p className={`text-xs ${tokens.text.muted} mt-1`}>Enter an emoji or leave empty for default</p>
          </div>

          <button
            onClick={() => {
              if (newListName.trim()) {
                createList(newListName, newListIcon);
              }
            }}
            disabled={!newListName.trim()}
            className={`w-full px-4 py-3 ${tokens.button.primary} font-semibold rounded-landing min-h-[48px] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            <Icon name="plus" size="sm" />
            <span>Create List</span>
          </button>
        </div>
      </BottomSheet>

      {/* Edit List Bottom Sheet */}
      <BottomSheet
        isOpen={showEditListSheet}
        onClose={() => {
          setShowEditListSheet(false);
          setEditingList(null);
          setEditListName('');
          setEditListIcon('');
        }}
        title="Edit List"
      >
        {editingList && (
          <div className="p-4 space-y-4">
            <div>
              <label className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                List Name *
              </label>
              <input
                type="text"
                className={`w-full px-3 py-2.5 ${tokens.border.light} border rounded-landing text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                value={editListName}
                onChange={(e) => setEditListName(e.target.value)}
                autoFocus
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                Icon (optional)
              </label>
              <input
                type="text"
                className={`w-full px-3 py-2.5 ${tokens.border.light} border rounded-landing text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                value={editListIcon}
                onChange={(e) => setEditListIcon(e.target.value)}
                maxLength={2}
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (editListName.trim()) {
                    updateList(editingList.listId, { name: editListName, icon: editListIcon });
                  }
                }}
                disabled={!editListName.trim()}
                className={`flex-1 px-4 py-3 ${tokens.button.primary} font-semibold rounded-landing min-h-[48px] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                <Icon name="check" size="sm" />
                <span>Save</span>
              </button>
              <button
                onClick={() => deleteList(editingList.listId)}
                className={`px-4 py-3 bg-red-600 text-white font-semibold rounded-landing min-h-[48px] flex items-center justify-center`}
              >
                <Icon name="delete" size="md" />
              </button>
            </div>
          </div>
        )}
      </BottomSheet>
      </div>
    </div>
  );
}

export default function MobileTodoScaleProposals() {
  const { isDarkMode } = useThemeStore();

  return <Proposal2Compact isDarkMode={isDarkMode} />;
}
